// ⌘K and ? (APS-195, docs/brand.md "Keyboard"): Find (Console D, APS-322; Find.tsx), and the
// list of the console's keys. Both are modal dialogs: the browser keeps focus inside, Esc closes,
// and focus goes back where it was.
import { useEffect, useId, useRef } from 'react'
import { useI18n } from '../i18n'
import { findKey, shortcutOff } from '../lib/keys'
import { Find, useModal } from './Find'

export type CommandLayer = 'find' | 'keys' | null

function Keys({ onClose }: { onClose: () => void }) {
  const { t } = useI18n()
  const ref = useModal(onClose)
  const titleId = useId()
  const row = (label: string, ...keys: string[]) => (
    <li key={label}>
      <span>{label}</span>
      <span translate="no">
        {keys.map((k) => (
          <kbd key={k}>{k}</kbd>
        ))}
      </span>
    </li>
  )
  return (
    <dialog ref={ref} className="pal keys" aria-labelledby={titleId}>
      <div className="keys-h">
        <h2 id={titleId}>{t('keys.title')}</h2>
        <button
          type="button"
          className="btn ghost sm"
          // Its name starts with the key it shows (WCAG 2.5.3).
          aria-label={t('keys.close')}
          onClick={() => ref.current?.close()}
        >
          <kbd translate="no">esc</kbd>
        </button>
      </div>
      {/* It scrolls on a short screen, so it takes focus (WCAG 2.1.1, axe scrollable-region). */}
      {/* biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be focusable */}
      <section className="keys-b" tabIndex={0} aria-labelledby={titleId}>
        <section>
          <h3>{t('keys.anywhere')}</h3>
          <ul>
            {row(t('keys.find'), findKey(), '/')}
            {row(t('keys.keys'), '?')}
            {row(t('keys.rail'), '[')}
            {row(t('keys.closeDialog'), 'esc')}
          </ul>
        </section>
        <section>
          <h3>{t('keys.lists')}</h3>
          <ul>
            {row(t('keys.rows'), 'j', 'k')}
            {row(t('issues.state.key.open'), '↵')}
            {row(t('issues.state.key.select'), 'x')}
          </ul>
        </section>
        <section>
          <h3>{t('keys.issue')}</h3>
          <ul>{row(t('issues.state.key.resolve'), 'e')}</ul>
        </section>
        <section>
          <h3>{t('keys.replay')}</h3>
          <ul>
            {row(t('keys.replay.play'), 'space')}
            {row(t('keys.replay.step'), '←', '→')}
            {row(t('keys.replay.ends'), 'home', 'end')}
            {row(t('keys.replay.keyframes'), 'pg up', 'pg dn')}
          </ul>
          <p className="keys-note">{t('keys.replay.cut')}</p>
        </section>
        <section className="keys-go">
          <h3>{t('keys.goTo')}</h3>
          <ul>
            {row(t('nav.home'), 'g', 'h')}
            {row(t('nav.agents'), 'g', 'n')}
            {row(t('nav.issues'), 'g', 'i')}
            {row(t('nav.releases'), 'g', 'r')}
            {row(t('nav.performance'), 'g', 'p')}
            {row(t('nav.people'), 'g', 'u')}
            {row(t('nav.sessions'), 'g', 's')}
            {row(t('nav.logs'), 'g', 'l')}
            {row(t('nav.settings'), 'g', ',')}
          </ul>
        </section>
      </section>
    </dialog>
  )
}

/**
 * The console's global keys and their two dialogs: ⌘K (Ctrl K) opens and closes Find, ? the list
 * of keys. Kept by the shell, which shows the buttons for them too.
 */
export function CommandBar({
  layer,
  setLayer,
  onRail,
}: {
  layer: CommandLayer
  setLayer: (layer: CommandLayer) => void
  /** Folds or opens the sidebar (one of Find's actions). */
  onRail: () => void
}) {
  const open = useRef(layer)
  open.current = layer
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setLayer(open.current === 'find' ? null : 'find')
      } else if (e.key === '?' && !shortcutOff(e)) {
        e.preventDefault()
        setLayer('keys')
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [setLayer])
  const close = () => setLayer(null)
  return layer === 'find' ? (
    <Find onClose={close} onKeys={() => setLayer('keys')} onRail={onRail} />
  ) : layer === 'keys' ? (
    <Keys onClose={close} />
  ) : null
}
